import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Public Pages
import Home from "./pages/Home";
import Courses from "./pages/Courses";
import Exams from "./pages/Exams";
import ExamDetails from "./pages/ExamDetails";
import LocationSEO from "./pages/LocationSEO";
import Features from "./pages/Features";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Resources from "./pages/Resources";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";

// Admin Pages
import AdminLogin from "./pages/admin/Login";
import AdminDashboard from "./pages/admin/Dashboard";
import ManageMaterials from "./pages/admin/ManageMaterials";
import ManageVideos from "./pages/admin/ManageVideos";
import ManageYouTube from "./pages/admin/ManageYouTube";
import ManageBlogV3 from "./pages/admin/ManageBlogV3";
import ManageTests from "./pages/admin/ManageTests";
import CurrentAffairsGenerator from "./pages/admin/CurrentAffairsGenerator";
import FixBlogDatabase from "./pages/admin/FixBlogDatabase";

// Student Pages
import StudentRegister from "./pages/StudentRegister";
import StudentLogin from "./pages/StudentLogin";
import StudentDashboard from "./pages/StudentDashboard";
import StudentBatches from "./pages/StudentBatches";
import StudentLiveClasses from "./pages/StudentLiveClasses";
import StudentTests from "./pages/StudentTests";
import StudentTestsEnhanced from "./pages/StudentTestsEnhanced";
import TakeTest from "./pages/TakeTest";
import TestResult from "./pages/TestResult";
import TestResultEnhanced from "./pages/TestResultEnhanced";
import TestReview from "./pages/TestReview";
import PerformanceDashboard from "./pages/PerformanceDashboard";

// AI Pages
import AIChatbot from "./pages/AIChatbot";
import AIStudyPlan from "./pages/AIStudyPlan";
import AIQuestionGenerator from "./pages/AIQuestionGenerator";

// PYQ Library
import PYQ from "./pages/PYQ";
import ManagePYQ from "./pages/admin/ManagePYQ";
import TypingTestList from "./pages/TypingTestList";
import TypingTest from "./pages/TypingTest";
import TypingResult from "./pages/TypingResult";
import LocalLandingPage from "./pages/LocalLandingPage";
import { LOCAL_PAGE_SLUGS } from "./config/localSeoPages";
import MaintenancePage from "./pages/MaintenancePage";
import { isMaintenanceActive } from "./config/maintenance";
import AIInterviewLanding from "./pages/AIInterviewLanding";
import AIInterviewSession from "./pages/AIInterviewSession";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import SeoApplier from "./components/SeoApplier";
import EnrollNow from "./pages/EnrollNow";
import EnquireNow from "./pages/EnquireNow";
import AdminLeads from "./pages/admin/AdminLeads";
import DailyQuiz from "./pages/DailyQuiz";

// Redirect old /blog/:id and /Latest_update/:id → /latest-updates/:id
function BlogRedirect() {
  const { id } = useParams();
  return <Navigate to={`/latest-updates/${id}`} replace />;
}

function App() {
  // Maintenance check runs once at app boot. Admin routes and the bypass
  // query-param are exempted inside isMaintenanceActive().
  if (isMaintenanceActive()) {
    return (
      <HelmetProvider>
        <div className="App">
          <MaintenancePage />
        </div>
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider>
      <div className="App">
        <AuthProvider>
          <BrowserRouter>
          <SeoApplier />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/courses" element={<Navigate to="/" replace />} />
            <Route path="/exams" element={<Exams />} />
            <Route path="/exams/:examId" element={<ExamDetails />} />
            <Route path="/best-coaching-karol-bagh-delhi" element={<LocationSEO />} />
            {LOCAL_PAGE_SLUGS.map((slug) => (
              <Route key={slug} path={`/${slug}`} element={<LocalLandingPage slug={slug} />} />
            ))}
            <Route path="/features" element={<Navigate to="/student-benefits" replace />} />
            <Route path="/student-benefits" element={<Features />} />
            <Route path="/why-choose-us" element={<Features />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Navigate to="/start-preparation" replace />} />
            <Route path="/start-preparation" element={<Contact />} />
            <Route path="/enroll-now" element={<EnrollNow />} />
            <Route path="/enquire-now" element={<EnquireNow />} />
            <Route path="/resources" element={<Navigate to="/study-materials" replace />} />
            <Route path="/study-materials" element={<Resources />} />
            <Route path="/pyq" element={<Navigate to="/previous-year-question-paper" replace />} />
            <Route path="/previous-year-question-paper" element={<PYQ />} />
            <Route path="/previous-year-papers" element={<Navigate to="/previous-year-question-paper" replace />} />
            <Route path="/typing-test" element={<TypingTestList />} />
            <Route path="/typing-test/:passageId" element={<TypingTest />} />
            <Route path="/typing-test/:passageId/result" element={<TypingResult />} />
            <Route path="/ai-interview" element={<AIInterviewLanding />} />
            <Route path="/ai-interview/session/:sessionId" element={<AIInterviewSession />} />
            <Route path="/daily-quiz" element={<DailyQuiz />} />
            {/* New canonical blog URLs (per latest spec) */}
            <Route path="/latest-updates" element={<Blog />} />
            <Route path="/latest-updates/:id" element={<BlogDetail />} />
            {/* Legacy URLs — redirect to new canonical paths so old inbound links & indexed pages don't 404 */}
            <Route path="/Latest_update" element={<Navigate to="/latest-updates" replace />} />
            <Route path="/Latest_update/:id" element={<BlogRedirect />} />
            <Route path="/blog" element={<Navigate to="/latest-updates" replace />} />
            <Route path="/blog/:id" element={<BlogRedirect />} />
            <Route path="/latest_update" element={<Navigate to="/latest-updates" replace />} />
            <Route path="/latest-update" element={<Navigate to="/latest-updates" replace />} />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/materials"
              element={
                <ProtectedRoute>
                  <ManageMaterials />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/videos"
              element={
                <ProtectedRoute>
                  <ManageVideos />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/youtube"
              element={
                <ProtectedRoute>
                  <ManageYouTube />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/blog"
              element={
                <ProtectedRoute>
                  <ManageBlogV3 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/leads"
              element={
                <ProtectedRoute>
                  <AdminLeads />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/current-affairs"
              element={
                <ProtectedRoute>
                  <CurrentAffairsGenerator />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/fix-blog"
              element={
                <ProtectedRoute>
                  <FixBlogDatabase />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/tests"
              element={
                <ProtectedRoute>
                  <ManageTests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pyq"
              element={
                <ProtectedRoute>
                  <ManagePYQ />
                </ProtectedRoute>
              }
            />
            
            {/* Student Routes */}
            <Route path="/student/register" element={<StudentRegister />} />
            <Route path="/student/login" element={<StudentLogin />} />
            <Route path="/student/dashboard" element={<StudentDashboard />} />
            <Route path="/student/batches" element={<StudentBatches />} />
            <Route path="/student/live-classes" element={<StudentLiveClasses />} />
            <Route path="/student/tests" element={<StudentTestsEnhanced />} />
            <Route path="/student/tests-legacy" element={<StudentTests />} />
            <Route path="/student/test/:testId" element={<TakeTest />} />
            <Route path="/student/test/:testId/result" element={<TestResultEnhanced />} />
            <Route path="/student/test/:testId/review" element={<TestReview />} />
            <Route path="/student/performance" element={<PerformanceDashboard />} />
            
            {/* AI Feature Routes */}
            <Route path="/student/ai-chatbot" element={<AIChatbot />} />
            <Route path="/student/ai-study-plan" element={<AIStudyPlan />} />
            <Route path="/student/ai-questions" element={<AIQuestionGenerator />} />
          </Routes>
        </BrowserRouter>
        <PWAInstallPrompt />
      </AuthProvider>
    </div>
    </HelmetProvider>
  );
}

export default App;
